<script setup lang="ts">
import ModalAgeVerification from '~/components/modals/ModalAgeVerification.vue'
import { useGeneralStore } from '~/store/index'

const generalStore = useGeneralStore()
const { locale, t: $t } = useI18n()
const route = useRoute()
const documentLangByLocale = {
	en: 'en-GB',
	pt: 'pt-PT',
	fr: 'fr-FR',
} as const
const documentLang = computed(() => documentLangByLocale[locale.value as keyof typeof documentLangByLocale] || 'en-GB')

const ogImage = 'https://d1p54ao86z4jzg.cloudfront.net/link_preview.webp'

useSeoMeta({
	// title y description se recalculan cada vez que cambie el locale
	title: () => $t('Your lucky number is searching for you'),
	ogTitle: () => $t('Your lucky number is searching for you'),
	ogSiteName: () => $t('Your lucky number is searching for you'),

	description: () =>
		$t('Access all the lottery data you need in one place and explore new chances. Ready to change your fortune?'),
	ogDescription: () =>
		$t('Access all the lottery data you need in one place and explore new chances. Ready to change your fortune?'),

	ogType: 'article',
	ogImage,
	ogImageWidth: 1200,
	ogImageHeight: 620,
})

useHead({
	htmlAttrs: {
		lang: documentLang.value,
	},
	script: [
		{
			key: 'age-gate-prehydration',
			tagPosition: 'head',
			tagPriority: 'critical',
			innerHTML: `(function () {
				if (window.__ageGatePreHydrationBound__) return;
				window.__ageGatePreHydrationBound__ = true;
				var modalSelector = '#ageVerificationModal, [data-age-modal], .age-modal';
				var getLocalePrefix = function (pathname) {
					var match = pathname.match(/^\\/(en|fr|pt)(?:\\/|$)/);
					return match ? '/' + match[1] : '';
				};
				var setAgeCookie = function (value) {
					var secure = window.location.protocol === 'https:' ? '; Secure' : '';
					document.cookie = 'age_verified=' + value + '; Max-Age=2592000; Path=/; SameSite=Lax' + secure;
				};
				var hideModal = function (modal) {
					if (!modal) return;
					modal.style.display = 'none';
					modal.setAttribute('hidden', 'hidden');
					modal.setAttribute('aria-hidden', 'true');
				};
				var setSnapshot = function (isAdult, pendingRedirect) {
					window.__age_gate_state__ = { acted: true, isAdult: isAdult, showModal: false, pendingRedirect: !!pendingRedirect };
					window.__age_modal_acted__ = true;
					if (pendingRedirect) window.__age_pending_redirect__ = true;
					else delete window.__age_pending_redirect__;
					window.dispatchEvent(new CustomEvent('age-gate:acted', { detail: { isAdult: isAdult, pendingRedirect: !!pendingRedirect } }));
				};
				document.addEventListener('click', function (event) {
					var target = event.target;
					if (!(target instanceof Element)) return;
					var actionEl = target.closest('[data-age-action]');
					if (!actionEl || window.__age_modal_acted__) return;
					var action = actionEl.getAttribute('data-age-action');
					var modal = actionEl.closest(modalSelector) || document.querySelector(modalSelector);
					if (action === 'confirm') {
						hideModal(modal);
						setAgeCookie('true');
						setSnapshot(true, false);
						return;
					}
					if (action === 'decline') {
						hideModal(modal);
						setAgeCookie('false');
						setSnapshot(false, true);
						window.location.assign(getLocalePrefix(window.location.pathname) + '/access-denied');
					}
				}, true);
			})();`,
		},
	],
})

const isOpenModal = computed<boolean>({
	get() {
		if (route.path.includes('/responsible-gaming')) {
			return false
		}

		return generalStore.show_modal_age_verification
	},
	set(value: boolean) {
		generalStore.SET_STATE_GENERAL({
			type: 'show_modal_age_verification',
			value,
		})
	},
})

watch(
	() => documentLang.value,
	(lang) => {
		if (!import.meta.client) return
		document.documentElement.setAttribute('lang', lang)
	},
	{ immediate: true },
)
</script>

<template>
	<NuxtPwaManifest />
	<ModalAgeVerification v-model="isOpenModal" />

	<ClientOnly>
		<OfflineBanner />
	</ClientOnly>

	<NuxtLayout>
		<NuxtPage />

		<ClientOnly>
			<UNotifications />
		</ClientOnly>
	</NuxtLayout>
</template>
